import React, { useState } from 'react'
import { COLORS, EmptyState, INPUT_STYLE, Kpi, ProgressBar, Stepper, addMonths, monthKey, monthLabelOf, pct, todayIso } from '../FieldOpsKit'
import { NO_ORDER_REASONS, REPS } from '../demoData'
import { routesOf, visitStats, visitsIn } from '../store'
import StoreDrawer from './StoreDrawer'

/**
 * Visit compliance: planned vs done visits by salesperson, route and store, the stores that were missed most and
 * why salespeople left without an order. Compliance = done ÷ (done + missed) among the visits already due.
 */
export default function ComplianceTab({ ctx }) {
    const sales = ctx.state.sales
    const cur = monthKey(todayIso())
    const [month, setMonth] = useState(cur)
    const [rep, setRep] = useState('all')
    const [drawer, setDrawer] = useState(null)

    const visits = visitsIn(sales, month, (v) => rep === 'all' || v.rep === rep)
    const byStore = {}
    sales.stores.forEach((s) => { byStore[s.id] = s })
    const total = visitStats(visits)
    const due = total.done + total.missed
    const orders = visits.filter((v) => v.status === 'done' && v.outcome === 'order').length
    const noOrders = visits.filter((v) => v.status === 'done' && v.outcome === 'no_order')

    const group = (keyFn, names) => names.map((n) => {
        const list = visits.filter((v) => keyFn(v) === n)
        return { name: n, stats: visitStats(list), orders: list.filter((v) => v.status === 'done' && v.outcome === 'order').length, done: list.filter((v) => v.status === 'done').length }
    }).filter((g) => g.stats.total > 0)
    const reps = group((v) => v.rep, REPS)
    const routes = group((v) => byStore[v.storeId].route, routesOf(sales)).sort((a, b) => pct(a.stats.done, a.stats.done + a.stats.missed) - pct(b.stats.done, b.stats.done + b.stats.missed))

    const missedBy = {}
    visits.forEach((v) => { if (v.status === 'missed' || (v.status === 'planned' && v.date < todayIso())) missedBy[v.storeId] = (missedBy[v.storeId] || 0) + 1 })
    const worst = Object.keys(missedBy).map((id) => ({ store: byStore[id], n: missedBy[id] })).sort((a, b) => b.n - a.n).slice(0, 8)
    const reasons = NO_ORDER_REASONS.map((r) => ({ r: r, n: noOrders.filter((v) => v.reason === r).length })).filter((x) => x.n > 0).sort((a, b) => b.n - a.n)

    const row = (g) => {
        const gDue = g.stats.done + g.stats.missed
        return (
            <tr key={g.name}>
                <td style={{ fontWeight: 700 }}>{g.name}</td><td>{g.stats.planned}</td><td>{g.stats.done}</td><td>{g.stats.missed}</td>
                <td style={{ minWidth: 150 }}><ProgressBar value={g.stats.done} max={gDue} /></td><td>{pct(g.orders, g.done)}%</td>
            </tr>
        )
    }

    return (
        <React.Fragment>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 }}>
                <Stepper dark label={monthLabelOf(month)} onPrev={() => setMonth(addMonths(month, -1))} onNext={() => setMonth(addMonths(month, 1))} canPrev={month > addMonths(cur, -2)} canNext={month < cur} />
                <select value={rep} onChange={(e) => setRep(e.target.value)} style={INPUT_STYLE} aria-label="Salesperson">
                    <option value="all">All salespeople</option>
                    {REPS.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
            </div>
            <div className="row g-3 mb-3">
                <div className="col-6 col-lg-3"><Kpi label="Compliance" value={pct(total.done, due) + '%'} sub={total.done + ' of ' + due + ' visits due'} status={pct(total.done, due) >= 80 ? 'success' : pct(total.done, due) >= 60 ? 'warning' : 'danger'} /></div>
                <div className="col-6 col-lg-3"><Kpi label="Visits done" value={total.done} sub={total.upcoming + ' still upcoming this month'} /></div>
                <div className="col-6 col-lg-3"><Kpi label="Missed" value={total.missed} sub={total.rescheduled + ' rescheduled'} status={total.missed > 0 ? 'danger' : 'success'} /></div>
                <div className="col-6 col-lg-3"><Kpi label="Visits with an order" value={pct(orders, total.done) + '%'} sub={orders + ' orders from ' + total.done + ' visits'} status="success" /></div>
            </div>

            {visits.length === 0 ? <EmptyState title="No visits in this period" /> : (
                <div className="row g-4">
                    <div className="col-lg-7">
                        <h6 className="card-title-clean mb-2">By salesperson</h6>
                        <div className="tableFixHeadSmall mb-4"><table className="table table-clean">
                            <thead><tr><th>Salesperson</th><th>Planned</th><th>Done</th><th>Missed</th><th>Compliance</th><th>Order rate</th></tr></thead>
                            <tbody>{reps.map(row)}</tbody>
                        </table></div>
                        <h6 className="card-title-clean mb-2">By route (lowest compliance first)</h6>
                        <div className="tableFixHeadSmall"><table className="table table-clean">
                            <thead><tr><th>Route</th><th>Planned</th><th>Done</th><th>Missed</th><th>Compliance</th><th>Order rate</th></tr></thead>
                            <tbody>{routes.map(row)}</tbody>
                        </table></div>
                    </div>
                    <div className="col-lg-5">
                        <div className="card mb-3"><div className="card-body">
                            <h6 className="card-title-clean mb-2">Stores missed most</h6>
                            {worst.length === 0 ? <div style={{ color: COLORS.muted, fontSize: '0.85rem' }}>No missed visits.</div> : worst.map((w) => (
                                <div key={w.store.id} onClick={() => setDrawer(w.store.id)} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderTop: '1px solid ' + COLORS.soft, fontSize: '0.85rem', cursor: 'pointer' }}>
                                    <span><strong style={{ color: COLORS.navy }}>{w.store.name}</strong><span style={{ color: COLORS.slate }}> · {w.store.rep}</span></span>
                                    <strong style={{ color: COLORS.red }}>{w.n}</strong>
                                </div>
                            ))}
                        </div></div>
                        <div className="card"><div className="card-body">
                            <h6 className="card-title-clean mb-2">Why there was no order</h6>
                            {reasons.length === 0 ? <div style={{ color: COLORS.muted, fontSize: '0.85rem' }}>No “no order” visits.</div> : reasons.map((x) => (
                                <div key={x.r} style={{ marginBottom: 8 }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem' }}><span>{x.r}</span><strong>{x.n}</strong></div>
                                    <ProgressBar value={x.n} max={reasons[0].n} height={6} color={COLORS.purple} hideLabel />
                                </div>
                            ))}
                        </div></div>
                    </div>
                </div>
            )}
            {drawer ? <StoreDrawer ctx={ctx} storeId={drawer} onClose={() => setDrawer(null)} /> : null}
        </React.Fragment>
    )
}
